import {
    Box,
    CurrencyInput,
    Flex,
    Radio,
    RadioGroup,
    PoundIcon,
    useBrand,
    BRANDS,
    THEME_CONSTANTS
} from '@cof/plastic-components';
import Group from '../../../components/Group';
import { DescriptionSection, LabelSection } from '../../../components/TextField/TextField';
import ErrorHandler, { shouldDisplayFormTick, shouldDisplayRadioTick } from './ErrorHandler';
import { InputWrapper } from './SharedStyledComponents';
import { Controller, useFormContext } from 'react-hook-form';
import { withText } from '../../../text/textStore';
import PropTypes from 'prop-types';
import { FORM_SECTIONS } from './FormIDs';
import { useContext, useEffect, useState } from 'react';
import { FormUtilsContext } from '../QuickCheck';
import { useFormData } from '../../../components/FormDataContext';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';
import { getAriaDescribedBy } from '../../../utilities/utils';

const otherIncomeOptions = {
    Yes: 'Y',
    No: 'N'
};

const employmentIDs = FORM_SECTIONS.Employment;

const getEditInitialRadioValue = (isEdit: any, formData: any) => {
    if (!isEdit) return undefined;
    return Number(formData?.formValues?.[employmentIDs.OtherIncomeAmount]) > 0 ? 'Y' : 'N';
};

const OtherIncomeQuestion = ({ Text, JsxText, Text: text }: any) => {
    const {
        register,
        formState: { errors, touchedFields },
        watch,
        getValues,
        setValue
    } = useFormContext();

    const brand = useBrand();
    const { isEdit, formData } = useFormData();
    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    const hasYesNo = brand !== BRANDS.ASOS.toLowerCase();
    const [hasOtherIncomeAmountFocused, setHasOtherIncomeAmountFocused] = useState(false);

    const otherIncomeRadio = watch(employmentIDs.OtherIncome);
    const showAmountInput = !hasYesNo || otherIncomeRadio === 'Y';

    const editInitialRadioValue = getEditInitialRadioValue(isEdit, formData);

    // Edit mode only: set initial radio state from pre-populated amount
    useEffect(() => {
        if (editInitialRadioValue) {
            setValue(employmentIDs.OtherIncome, editInitialRadioValue);
        }
    }, [editInitialRadioValue, setValue]);

    const DescriptionText = () => (
        <DescriptionSection
            description={text({ path: 'HouseHoldIncome.Description' })}
            id={employmentIDs.OtherIncomeAmount}
        ></DescriptionSection>
    );

    return (
        <InputWrapper data-qa-id={`${employmentIDs.OtherIncomeAmount}-input-wrapper`}>
            {hasYesNo && (
                <Group as={'fieldset'}>
                    <Flex>
                        <RadioGroup
                            paddingTop={'xs'}
                            name={employmentIDs.OtherIncome}
                            variant="square"
                            optionWrapperProps={{
                                width: '9.7rem',
                                // @ts-expect-error TS2322: Type '"0"' is not assignable to type 'ResponsiveValue<AllowedSpace, DefaultTheme> | undefined'.
                                marginBottom: '0',
                                marginTop: THEME_CONSTANTS.SPACE.SM
                            }}
                            aria-describedby={getAriaDescribedBy(
                                employmentIDs.OtherIncome,
                                submitAttempted,
                                [`${employmentIDs.OtherIncomeAmount}-helper-text`],
                                errors
                            )}
                        >
                            <LabelSection
                                id={employmentIDs.OtherIncome}
                                label={<Text path="HouseHoldIncome.Heading"></Text>}
                                pt={'xs'}
                                as="legend"
                                // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                                displayTick={shouldDisplayRadioTick(employmentIDs.OtherIncome, watch, errors)}
                                data-qm-allow
                            />
                            <DescriptionText />
                            {Object.entries(otherIncomeOptions).map(([displayName, value]) => (
                                <Radio
                                    key={`${employmentIDs.OtherIncome}-${value}`}
                                    value={value}
                                    {...register(employmentIDs.OtherIncome)}
                                    onChange={() => {
                                        setValue(employmentIDs.OtherIncome, value);
                                        setValue(employmentIDs.OtherIncomeAmount, '');
                                    }}
                                    data-qm-allow
                                >
                                    {displayName}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </Flex>
                </Group>
            )}
            <Box
                display={showAmountInput ? 'block' : 'none'}
                data-qa-id={`${employmentIDs.OtherIncomeAmount}-container`}
            >
                <Group>
                    <LabelSection
                        id={employmentIDs.OtherIncomeAmount}
                        label={<Text path="AdditionalIncome"></Text>}
                        pt={'xs'}
                        displayTick={shouldDisplayFormTick(employmentIDs.OtherIncomeAmount, getValues, errors)}
                        data-qm-allow
                    />
                    {!hasYesNo && <DescriptionText />}
                    <Controller
                        name={employmentIDs.OtherIncomeAmount}
                        rules={{
                            pattern: { value: /^\d+$/, message: 'This is not a valid other household income' },
                            maxLength: { value: 7, message: 'Other household income must be a max of 7 digits' }
                        }}
                        render={({ field }: any) => {
                            const additionalIDs = [`${employmentIDs.OtherIncomeAmount}-helper-text`];
                            if (hasOtherIncomeAmountFocused) {
                                additionalIDs.push(`${employmentIDs.OtherIncomeAmount}-explanation-box-text`);
                            }
                            return (
                                <CurrencyInput
                                    id={employmentIDs.OtherIncomeAmount}
                                    name={field.name}
                                    value={field.value ?? ''}
                                    onValueChange={(value: any) => {
                                        field.onChange(value ?? '');
                                    }}
                                    onFocus={() => setHasOtherIncomeAmountFocused(true)}
                                    onBlur={field.onBlur}
                                    data-qa-id={employmentIDs.OtherIncomeAmount}
                                    allowDecimals={false}
                                    allowNegativeValue={false}
                                    variant={variantTernary(employmentIDs.OtherIncomeAmount)}
                                    aria-describedby={getAriaDescribedBy(
                                        employmentIDs.OtherIncomeAmount,
                                        submitAttempted,
                                        additionalIDs,
                                        errors
                                    )}
                                    explanation={{
                                        Icon: PoundIcon,
                                        body: (
                                            <span id={`${employmentIDs.OtherIncomeAmount}-explanation-box-text`}>
                                                <JsxText
                                                    path="YearlyIncome.Explanation"
                                                    params={{
                                                        BOLD_TEXT: <b>go down</b>
                                                    }}
                                                ></JsxText>
                                            </span>
                                        ),
                                        textId: 'explanation-text',
                                        prefix: employmentIDs.OtherIncomeAmount
                                    }}
                                />
                            );
                        }}
                    />
                    <ErrorHandler
                        id={employmentIDs.OtherIncomeAmount}
                        show={touchedFields[employmentIDs.OtherIncomeAmount]}
                        data-qm-allow
                    />
                </Group>
            </Box>
        </InputWrapper>
    );
};

OtherIncomeQuestion.propTypes = {
    Text: PropTypes.func.isRequired,
    JsxText: PropTypes.func.isRequired
};

export default withText('Employment')(OtherIncomeQuestion);
